import React, { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Catches React render errors and displays them instead of a blank screen.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div className="min-h-screen bg-background text-foreground p-8 flex items-center justify-center">
          <div className="max-w-2xl w-full rounded-lg border border-destructive/50 bg-destructive/5 p-6">
            <h1 className="text-xl font-semibold text-destructive mb-2">
              Something went wrong
            </h1>
            <p className="text-sm text-muted-foreground mb-4">
              The app crashed. Check the browser console for details.
            </p>
            <pre className="text-xs bg-muted p-4 rounded overflow-auto max-h-48">
              {this.state.error.toString()}
              {this.state.errorInfo?.componentStack && (
                <>
                  {"\n\nComponent stack:"}
                  {this.state.errorInfo.componentStack}
                </>
              )}
            </pre>
            <button
              type="button"
              className="mt-4 px-4 py-2 rounded bg-primary text-primary-foreground text-sm font-medium hover:opacity-90"
              onClick={() =>
                this.setState({ hasError: false, error: null, errorInfo: null })
              }
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
